import {AllowedFileExtensionsAiChatUploads, AllowedMimeTypesAiChatUploads} from "../../serverClientShared/sharedEnums";
import {marked} from 'marked';
import * as HtmlParsing from "./HtmlParsing";
import {aiStore} from "../../index";
import * as DocxParsing from "./DocxParsing";
import * as OdtParsing from "./OdtParsing";
import * as RtfParsing from "./RtfParsing";
import * as CsvParsing from "./CsvParsing";
import * as ExcelParsing from "./ExcelParsing";
import * as PdfParsing from "./PdfParser"
import axios from "axios";

export default class ParsingUtility {
    static getFileExtension(file) {
        if (file.FileExtension) {
            return file.FileExtension;
        } else {
            const parts = file.name.split('.');
            return parts.length > 1 ? parts.pop().toLowerCase() : '';
        }
    }
    static nonMediaTypes = [
        AllowedFileExtensionsAiChatUploads.MD,
        AllowedFileExtensionsAiChatUploads.TXT,
        AllowedFileExtensionsAiChatUploads.PDF,
        AllowedFileExtensionsAiChatUploads.DOCX,
        AllowedFileExtensionsAiChatUploads.HTML,
        AllowedFileExtensionsAiChatUploads.CSV,
        AllowedFileExtensionsAiChatUploads.RTF,
        AllowedFileExtensionsAiChatUploads.ODT,
        AllowedFileExtensionsAiChatUploads.XLS,
        AllowedFileExtensionsAiChatUploads.XLSX
    ];

    static async parseFileByType(fileObject, isFromLibrary, isEmbedding, abortedFiles= new Set()) {
        let parsedChunksArray
        const file = fileObject?.file ?? fileObject;
        const fileType = this.getFileExtension(file);
        let fileToParse = fileObject
        if (isFromLibrary && this.nonMediaTypes.includes(fileType)) {
            fileToParse = await this.fetchFileFromLibrary(file)
        }
        switch (fileType) {
            case AllowedFileExtensionsAiChatUploads.MD:
                parsedChunksArray = await this.markdownFileToHtmlString(fileToParse, isEmbedding, abortedFiles);
                break;
            case AllowedFileExtensionsAiChatUploads.TXT:
                parsedChunksArray = await this.convertTxtToChunks(fileToParse, isEmbedding, abortedFiles);
                break;
            case AllowedFileExtensionsAiChatUploads.PDF:
            case AllowedFileExtensionsAiChatUploads.DOCX:
            case AllowedFileExtensionsAiChatUploads.HTML:
            case AllowedFileExtensionsAiChatUploads.CSV:
            case AllowedFileExtensionsAiChatUploads.RTF:
            case AllowedFileExtensionsAiChatUploads.ODT:
            case AllowedFileExtensionsAiChatUploads.XLS:
            case AllowedFileExtensionsAiChatUploads.XLSX:
                parsedChunksArray = await this.convertFileToHtml(fileToParse, fileType, isEmbedding, abortedFiles);
                break;
            case AllowedFileExtensionsAiChatUploads.IMAGE:
            case AllowedFileExtensionsAiChatUploads.PNG:
            case AllowedFileExtensionsAiChatUploads.JPG:
            case AllowedFileExtensionsAiChatUploads.JPEG:
            case AllowedFileExtensionsAiChatUploads.GIF:
            case AllowedFileExtensionsAiChatUploads.MP4:
            case AllowedFileExtensionsAiChatUploads.VIDEO:
                parsedChunksArray = this.convertMediaToHtml(fileToParse, isEmbedding, abortedFiles);
                break;
            default:
                return null;
        }
        if (!isEmbedding) {
            return parsedChunksArray.messages;
        }
        return parsedChunksArray;
    }

    static constructFileMessages = (file, parsedResult, fileId) => {
        const messages = [];
        const fileName = file.FileName ? file.FileName + '.' + file.FileExtension : file.name;
        const fileUrl = fileId || false;
        parsedResult.map((chunk,i) => {
            messages.push({
                role: 'user',
                content: (i=== 0 ? `This is a parsed ${fileName} file, the file name: " ${fileName}", ${fileUrl ? `the FILE URL : ${fileUrl}`: ''}, [1/${parsedResult.length}]} \n\n` : `Continuation of the parsed File "${fileName}" [${i+1}/${parsedResult.length}]\n\n` )+chunk,

            });
        })
        return {messages, parsedResult};
    }

    static async convertTxtToChunks(fileObject, isEmbedding, abortedFiles, chunkSize = 100) {
        const file = fileObject?.file ?? fileObject;
        const text = await file.text();
        const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
        const chunks = [];
        if (!isEmbedding){
            aiStore.sendAiProgressLoaderValue(file.name, 50);
        }
        for (let i = 0; i < lines.length; i += chunkSize) {
            const chunkLines = lines.slice(i, i + chunkSize);
            chunks.push(chunkLines.join('\n'));
        }
        return this.constructFileMessages(file, chunks, fileObject?.FileId)
    }

    static async markdownFileToHtmlString(fileObject, isEmbedding, abortedFiles) {
        const file = fileObject?.file || fileObject;
        const markdownText = await file.text();
        const htmlString = marked(markdownText);
        const chunks = await HtmlParsing.convertHtmlToString(
            { name: file.name, size: file.size },
            htmlString
        );
        return this.constructFileMessages(file, chunks, fileObject?.FileId)
    }
    static async convertFileToHtml(fileObject, fileType, isEmbedding, abortedFiles) {
        const file = fileObject?.file || fileObject;
        const parsingMethod = this.parsingMethods[fileType];
        if (!parsingMethod) {
            throw new Error(`Unsupported file type: ${fileType}`);
        }

        const chunks = await parsingMethod(file, isEmbedding, abortedFiles)
        return this.constructFileMessages(file, chunks, fileObject?.FileId);
    }
    static parsingMethods = {
        html: (file, isEmbedding, abortedFiles) => HtmlParsing.processHtmlFile(file, isEmbedding, abortedFiles),
        docx: (file, isEmbedding, abortedFiles) => DocxParsing.convertDocxToHtml(file, isEmbedding, abortedFiles),
        odt: (file, isEmbedding, abortedFiles) => OdtParsing.convertOdtToHtml(file, isEmbedding, abortedFiles),
        rtf: (file,isEmbedding, abortedFiles) => RtfParsing.convertRtfToHtml(file, isEmbedding, abortedFiles),
        csv: (file, isEmbedding, abortedFiles) => CsvParsing.convertCsvToHtml(file, isEmbedding, abortedFiles),
        xls: (file, isEmbedding, abortedFiles) => ExcelParsing.convertExcelToHtml(file, isEmbedding, abortedFiles),
        xlsx: (file,isEmbedding, abortedFiles) => ExcelParsing.convertExcelToHtml(file, isEmbedding, abortedFiles),
        pdf: (file, isEmbedding, abortedFiles) => PdfParsing.parsePdf(file, isEmbedding, abortedFiles),
    };

    static convertMediaToHtml(file){
        let mediaHtml
        if (file.FileType === 'video'){
            mediaHtml = `<video src="${file._id}" alt="${file.FileName} url=${file.FileId}"/>`
        } else {
            mediaHtml = `<img src="${file._id}" alt="${file.FileName} url=${file.FileId}"/>`
        }
        return this.constructFileMessages(file,[mediaHtml])

    }

    static async fetchFileFromLibrary(fileJson) {

        const ext = fileJson.FileExtension?.toLowerCase();
        let mimeType = AllowedMimeTypesAiChatUploads.OCTET_STREAM;
        if (ext === AllowedFileExtensionsAiChatUploads.MD) mimeType = AllowedMimeTypesAiChatUploads.MARKDOWN;
        else if (ext === AllowedFileExtensionsAiChatUploads.TXT) mimeType = AllowedMimeTypesAiChatUploads.PLAIN;
        else if (ext === AllowedFileExtensionsAiChatUploads.CSV) mimeType = AllowedMimeTypesAiChatUploads.CSV;
        else if (ext === AllowedFileExtensionsAiChatUploads.HTML) mimeType = AllowedMimeTypesAiChatUploads.HTML;
        else if ([AllowedFileExtensionsAiChatUploads.JPG, AllowedFileExtensionsAiChatUploads.JPEG].includes(ext)) mimeType = AllowedMimeTypesAiChatUploads.JPEG;
        else if (ext === AllowedFileExtensionsAiChatUploads.PNG) mimeType = AllowedMimeTypesAiChatUploads.PNG;
        else if (ext === AllowedFileExtensionsAiChatUploads.GIF) mimeType = AllowedMimeTypesAiChatUploads.GIF;
        else if (ext === AllowedFileExtensionsAiChatUploads.PDF) mimeType = AllowedMimeTypesAiChatUploads.PDF;

        if (["md", "txt", "csv", "html"].includes(ext)) {
            const res = await axios.get(fileJson.FileId, {responseType: "text"});
            return new File([res.data], fileJson.FileName, {type: mimeType});
        }
        const fileName = fileJson.OriginalFileName ? fileJson.OriginalFileName +'.'+fileJson.FileExtension : fileJson.FileName+'.'+fileJson.FileExtension;

        const res = await axios.get(fileJson.FileId, {responseType: "arraybuffer"});
        return {file: new File([res.data], fileName, {type: mimeType}), FileId: fileJson.FileId};

    }
}